/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { MemberService } from 'app/cd/services/member-service';
import { apiCall } from 'app/shared/lib/essette-api';
import { Toaster } from 'app/shared/lib/toaster';
import { EventAggregator } from 'aurelia-event-aggregator';
import { IBaseConfig } from 'app/core/models/essette-interfaces';
import { forceNull } from 'app/shared/lib/utilities';
import { FlagOptions } from 'app/core/components/races-ethnicities/races';
import { I18N } from 'aurelia-i18n';
import * as CD from 'app/cd/models/essette-api';

interface CustomSelectOption {
  display: string;
  enabled: boolean;
}

@autoinject()
export class SaveRace extends ComponentBase implements IForm {

  ////****************** Fields and Properties ************************************////
  @bindable.object({ defaultBindingMode: bindingMode.twoWay, changeHandler: 'reset' })
  public race: CD.Race = {};
  protected raceId: string | null = null;

  @bindable
  protected config: IBaseConfig = {};

  protected raceText: string = '';
  protected raceCodeText: string = '';

  protected flags: Array<CustomSelectOption> = [];

  // Drop Down Options
  private flagOptions: Array<CustomSelectOption> = [];

  ////****************** Lifecycle events (in order of execution) *****************////
  constructor(
    private readonly service: MemberService,
    private readonly toaster: Toaster,
    private readonly events: EventAggregator,
    private readonly i18n: I18N,
    ...rest: any[]) {
    super(...rest);
  }

  protected bind(): void {
    if(!this.config) { throw new Error('Bindable property [config] was not set'); }

    this.service.configure(this.config);

    const initialFlagOptions = <string[]>Object.values(FlagOptions).slice(0, 2);
    initialFlagOptions.forEach(option => {
      this.flagOptions.push({
        display: this.i18n.tr(option),
        enabled: false
      });
    });

    this.reset();
  }

  ////****************** Events ***************************************************////

  ////****************** Functions ************************************************////
  protected buildRace(): CD.Race {
    let availableAsBirthRace: boolean = false, availableAsIdentity: boolean = false;

    this.flags.map(flag => flag.display).forEach(flag => {
      switch(flag) {
        case this.i18n.tr(FlagOptions.availableAsBirthRace):
          availableAsBirthRace = true;
          break;
        case this.i18n.tr(FlagOptions.availableAsIdentity):
          availableAsIdentity = true;
          break;
      }
    });

    const saveRace: CD.Race = {
      id: this.race.id,
      description: this.raceText,
      code: forceNull(this.raceCodeText),
      flag: {
        availableAsBirthRace: availableAsBirthRace,
        availableAsIdentity: availableAsIdentity
      }
    };

    return saveRace;
  }

  ////****************** IForm ****************************************************////
  public hasChanges(): boolean {
    throw new Error('Method not implemented.');
  }

  public async submit(): Promise<boolean> {
    const isSuccess = await this.saveRace();
    return isSuccess;
  }

  public reset(): void {
    if(!this.race) return;

    this.raceId = this.race.id || null
    this.raceText = this.race.description || '';
    this.raceCodeText = this.race.code || '';
    this.flags = [];

    const flags = this.race.flag;
    if(flags) {
      // Set Flags
      this.flagOptions.forEach(option => {
        if(option.display === this.i18n.tr(FlagOptions.availableAsBirthRace) && flags.availableAsBirthRace) {
          this.flags.push(option);
        }
        if(option.display === this.i18n.tr(FlagOptions.availableAsIdentity) && flags.availableAsIdentity) {
          this.flags.push(option);
        }
      });
    }
  }

  ////****************** Service Calls ********************************************////
  @apiCall
  protected async saveRace(): Promise<boolean> {
    const insertRace: CD.Race = this.buildRace();

    let isSuccessful = false;
    try {
      this.state.beginSave();
      const response = await this.service.saveRace(insertRace);
      isSuccessful = response.isSuccessful;

      return isSuccessful;
    }
    catch(error) {
      // if duplicate constraint violated on race code, show toast
      if(error.status.errors) {
        const invalidId = error.status.errors.some((err: any) => err.code === 'property.constraintViolated' && err.property === 'code');
        if(invalidId) this.toaster.error().message(this.i18n.tr('ui:messages.warnings.inUse', { what: this.i18n.tr('cd:member.race.code.$long') })).show();
      }
      throw error;
    }
    finally {
      if(this.raceId) {
        this.toaster.standard.updated('cd:member.race.$', isSuccessful);
      } else {
        this.toaster.standard.created('cd:member.race.$', isSuccessful);
      }
      this.state.endSave();
    }
  }
}
